import type { FirmLibraryItem } from "@contract/library/library";
import { Box, Button, Group, Image, Modal, Stack, Tabs, Text, TextInput } from "@mantine/core";
import * as React from "react";
import { useMe } from "../api/MeContext";
import * as libraryApi from "../api/library";
import { DangerButton, SecondaryButton } from "../ui/buttons";
import { DataTable, type DataColumn } from "../ui/DataTable";
import { LinkAction, LinkActions } from "../ui/LinkAction";
import { MetaText } from "../ui/MetaText";
import { Notice } from "../ui/Notice";
import { PageHeader } from "../ui/PageHeader";
import { PageLoader } from "../ui/PageLoader";
import { PANEL_COLOR, PANEL_SPACE } from "../ui/tokens";
import { useFirmLibrary } from "./useFirmLibrary";

type Api = typeof libraryApi;

const dateOf = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/** The usage line admins see beside the tabs: the firm's saved components against its
 * plan's limit (Figma: "3 of 5 saved components used"). */
export function usageLine(used: number, limit: number | null): string {
  const noun = used === 1 ? "saved component" : "saved components";
  return limit === null ? `${used} ${noun}, no limit on your plan` : `${used} of ${limit} saved components used`;
}

/** The frame's thumbnail slot: 122 × 69, a `gray/1` block until the image loads. */
const THUMB = { w: 122, h: 69 };

function Thumb({ api, id }: { api: Api; id: string }) {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    let created: string | null = null;
    void api.fetchThumbUrl(id).then((next) => {
      if (!alive) {
        if (next) URL.revokeObjectURL(next);
        return;
      }
      created = next;
      setUrl(next);
    });
    // The object URL is released when the row unmounts or the list reloads it.
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [api, id]);
  return url ? (
    <Image src={url} alt="" w={THUMB.w} h={THUMB.h} fit="contain" bg={PANEL_COLOR.fill} />
  ) : (
    <Box w={THUMB.w} h={THUMB.h} bg={PANEL_COLOR.fill} />
  );
}

function columnsFor(
  api: Api,
  { sharedBy, onRename, onRemove }: { sharedBy: boolean; onRename?: (item: FirmLibraryItem) => void; onRemove?: (item: FirmLibraryItem) => void },
): DataColumn<FirmLibraryItem>[] {
  const cols: DataColumn<FirmLibraryItem>[] = [
    { key: "preview", header: "Preview", render: (item) => <Thumb api={api} id={item.id} /> },
    {
      key: "name",
      header: "Name",
      render: (item) => (
        <Stack gap={2}>
          <Text lh={1.25}>{item.name}</Text>
          <Text fz="xs" c="dimmed" lh={1.25}>
            {item.type === "element" ? "Element" : "Slide"}
          </Text>
        </Stack>
      ),
    },
  ];
  if (sharedBy) cols.push({ key: "shared", header: "Shared By", render: (item) => item.sharedByEmail ?? "A former member" });
  cols.push({ key: "date", header: "Date", render: (item) => dateOf(item.createdAt) });
  if (onRename || onRemove) {
    cols.push({
      key: "actions",
      header: "Actions",
      render: (item) => (
        <LinkActions>
          {onRename ? <LinkAction label={`Rename ${item.name}`} onClick={() => onRename(item)}>Rename</LinkAction> : null}
          {onRemove ? <LinkAction label={`Remove ${item.name}`} onClick={() => onRemove(item)}>Remove</LinkAction> : null}
        </LinkActions>
      ),
    });
  }
  return cols;
}

/** Component Library (firm-model spec §8, §10.2; Figma 15185:118): the firm library and
 * your own saved components. Admins (`edit_library`) rename and remove firm-library
 * components and see the firm's usage; sharing happens in PowerPoint. */
export function LibraryPage({ api = libraryApi }: { api?: Api }) {
  const { state: meState } = useMe();
  const me = meState.status === "ready" ? meState.me : null;
  const canEdit = Boolean(me?.capabilities.includes("edit_library"));
  const { list, loadError, actionError, clearActionError, busy, rename, remove } = useFirmLibrary(api);
  const [renaming, setRenaming] = React.useState<FirmLibraryItem | null>(null);
  const [newName, setNewName] = React.useState("");
  const [removing, setRemoving] = React.useState<FirmLibraryItem | null>(null);

  if (loadError) return <Notice tone="danger">{loadError}</Notice>;
  if (!list) return <PageLoader />;

  const firmColumns = columnsFor(api, {
    sharedBy: true,
    onRename: canEdit
      ? (item) => {
          setNewName(item.name);
          setRenaming(item);
        }
      : undefined,
    onRemove: canEdit ? setRemoving : undefined,
  });

  return (
    <Stack gap={PANEL_SPACE.page}>
      <PageHeader title="Component Library" />
      {actionError ? (
        <Notice tone="danger" onClose={clearActionError}>
          {actionError}
        </Notice>
      ) : null}
      <Tabs variant="outline" defaultValue="firm">
        <Tabs.List pl={PANEL_SPACE.field}>
          <Tabs.Tab value="firm">Firm Library ({list.firm.length})</Tabs.Tab>
          <Tabs.Tab value="mine">My Saved ({list.mine.length})</Tabs.Tab>
          {canEdit ? (
            <Group ml="auto" pr={PANEL_SPACE.field} align="center">
              <MetaText>{usageLine(list.used, list.limit)}</MetaText>
            </Group>
          ) : null}
        </Tabs.List>
        <Tabs.Panel value="firm" pt={PANEL_SPACE.page}>
          <DataTable
            label="Firm library"
            columns={firmColumns}
            rows={list.firm}
            rowKey={(item) => item.id}
            empty="Nothing in your firm library yet. Share a component from the Library in PowerPoint."
          />
        </Tabs.Panel>
        <Tabs.Panel value="mine" pt={PANEL_SPACE.page}>
          <DataTable
            label="My saved components"
            columns={columnsFor(api, { sharedBy: false })}
            rows={list.mine}
            rowKey={(item) => item.id}
            empty="You haven't saved any components yet. Save one from the Library in PowerPoint."
          />
        </Tabs.Panel>
      </Tabs>

      <Modal opened={renaming !== null} onClose={() => setRenaming(null)} title="Rename Component">
        <Stack gap="md">
          <TextInput label="Name" value={newName} maxLength={120} onChange={(e) => setNewName(e.currentTarget.value)} data-autofocus />
          <Group justify="flex-end" gap={5}>
            <SecondaryButton onClick={() => setRenaming(null)} disabled={busy}>
              Cancel
            </SecondaryButton>
            <Button
              loading={busy}
              disabled={!newName.trim()}
              onClick={() => {
                if (!renaming) return;
                void rename(renaming.id, newName.trim()).then((ok) => ok && setRenaming(null));
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={removing !== null} onClose={() => setRemoving(null)} title="Remove from the Firm Library">
        <Stack gap="md">
          <Text>
            Remove “{removing?.name}” from your firm's library? Nobody in the firm will see it anymore. The copy the person
            who shared it saved stays in their own components.
          </Text>
          <Group justify="flex-end" gap={5}>
            <SecondaryButton onClick={() => setRemoving(null)} disabled={busy}>
              Cancel
            </SecondaryButton>
            <DangerButton
              loading={busy}
              onClick={() => {
                if (!removing) return;
                void remove(removing.id).then((ok) => ok && setRemoving(null));
              }}
            >
              Remove
            </DangerButton>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
